import { createPaginationEntity } from '../paginationEntity/createPaginationEntity';
import type {
    CreateEntityOptions,
    CreatePayloadArgObject,
    CreatePayloadCreatorType,
    DeleteByIdPayloadCreatorType,
    DeleteByIdReturn,
    GetPayloadArg,
    PayloadCreator,
    QueryAllPayloadCreatorType,
    QueryByIdPayloadArgObject,
    QueryByIdPayloadCreatorType,
    UpdateByIdPayloadCreatorType,
} from './createEntityTypes';
import type { UnwrapPromise } from '../utils/internalTypings';
import { buildCombinedArgumentFromIdAndParams } from './createEntityUtils';
import type {
    DeleteByIdArg,
    QueryParamsBaseType,
} from '../paginationEntity/createPaginationEntityTypes';

export const createPaginationEntityAdapter = <
    Entity,
    QueryAllPayloadCreator extends QueryAllPayloadCreatorType<
        Entity,
        any
    > = QueryAllPayloadCreatorType<Entity, object>,
    QueryByIdPayloadCreator extends QueryByIdPayloadCreatorType<Entity> = PayloadCreator<
        Entity,
        string | QueryByIdPayloadArgObject
    >,
    UpdateByIdPayloadCreator extends
        UpdateByIdPayloadCreatorType<Entity> = UpdateByIdPayloadCreatorType<Entity>,
    CreatePayloadCreator extends CreatePayloadCreatorType<Entity> = PayloadCreator<
        Entity,
        Partial<Entity> | CreatePayloadArgObject<Entity>
    >,
    DeleteByIdPayloadCreator extends DeleteByIdPayloadCreatorType = PayloadCreator<
        DeleteByIdReturn,
        DeleteByIdArg
    >,
>({
    config,
    paginationEntityName,
}: {
    config: CreateEntityOptions<
        Entity,
        QueryAllPayloadCreator,
        QueryByIdPayloadCreator,
        UpdateByIdPayloadCreator,
        CreatePayloadCreator,
        DeleteByIdPayloadCreator
    >;
    paginationEntityName: string;
}) => {
    const { skipParamsHashing = true } = config;

    return createPaginationEntity<
        Entity,
        GetPayloadArg<QueryAllPayloadCreator, QueryParamsBaseType>,
        QueryByIdPayloadCreator,
        unknown,
        GetPayloadArg<CreatePayloadCreator>,
        GetPayloadArg<UpdateByIdPayloadCreator, any>,
        GetPayloadArg<DeleteByIdPayloadCreator, any>,
        UnwrapPromise<ReturnType<DeleteByIdPayloadCreator>>
    >({
        name: paginationEntityName,
        selectId: config.selectId,
        cacheDuration: config.cacheDuration,
        payloadCreators: {
            queryPage: async (params) => {
                if (!config.payloadCreators.queryAll) {
                    throw new Error('Query all is not supported');
                }
                delete params.pageNumber;
                delete params.pageSize;
                const response = await config.payloadCreators.queryAll!(params);
                return {
                    pageNumber: 0,
                    pageCount: 1,
                    data: response ?? [],
                };
            },
            queryById: (id: string, params?: Record<string, any>) => {
                if (!config.payloadCreators.queryById) {
                    throw new Error('Query by id is not supported');
                }
                return config.payloadCreators.queryById!(
                    // @ts-expect-error - can be either string or composite object
                    buildCombinedArgumentFromIdAndParams(id, params),
                );
            },
            create: (params) => {
                if (!config.payloadCreators.create) {
                    throw new Error('create is not supported');
                }
                return config.payloadCreators.create(
                    params as unknown as Partial<Entity> & CreatePayloadArgObject<Entity>,
                );
            },
            updateById: (payload) => {
                if (!config.payloadCreators.updateById) {
                    throw new Error('update is not supported');
                }
                return config.payloadCreators.updateById(payload);
            },
            deleteById: (payload: DeleteByIdArg) => {
                if (!config.payloadCreators.deleteById) {
                    throw new Error('delete is not supported');
                }
                // @ts-expect-error - can be either string or composite object
                return config.payloadCreators.deleteById(payload);
            },
        },
        localListsSynchronisation: true,
        skipParamsHashing,
    });
};
